import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, CircularProgress, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Link as MuiLink, ToggleButton, ToggleButtonGroup, FormGroup,
  FormControlLabel, Checkbox, Tooltip, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend,
} from 'recharts';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';

const LOOKBACK_OPTIONS = [
  { value: 1, label: '1Y' },
  { value: 2, label: '2Y' },
  { value: 5, label: '5Y' },
  { value: 10, label: '10Y' },
  { value: 30, label: 'Max' },
];

const SERIES_COLORS = {
  DFF: '#1976d2', DFEDTARU: '#7b1fa2', DFEDTARL: '#9c27b0',
  SOFR: '#0288d1', DPRIME: '#c62828',
  DGS1MO: '#00897b', DGS3MO: '#43a047', DGS6MO: '#7cb342',
  DGS1: '#fb8c00', DGS2: '#f4511e', DGS5: '#6d4c41',
  DGS10: '#5e35b1', DGS30: '#3949ab',
};

const DEFAULT_SELECTED = ['DFF', 'DGS2', 'DGS10', 'DGS30'];

// Plain-language explanations, surfaced via (i) tooltips and the glossary dialog.
const SERIES_DESCRIPTIONS = {
  DFF: 'The actual overnight interest rate at which U.S. banks lend reserves to each other. The Fed steers this rate to influence borrowing costs across the entire economy.',
  DFEDTARU: 'The upper bound of the Federal Reserve\'s target range for the federal funds rate — the highest level the Fed wants overnight bank lending to trade at.',
  DFEDTARL: 'The lower bound of the Federal Reserve\'s target range for the federal funds rate — the lowest level the Fed wants overnight bank lending to trade at.',
  SOFR: 'Secured Overnight Financing Rate — the cost of borrowing cash overnight using U.S. Treasuries as collateral. It has replaced LIBOR as the main benchmark for many loans and derivatives.',
  DPRIME: 'The interest rate that large U.S. banks charge their most creditworthy corporate customers. Many consumer loans (credit cards, HELOCs) are priced as "prime + a spread".',
  DGS1MO: 'Yield on a U.S. Treasury bill that matures in 1 month. Reflects very short-term, risk-free borrowing costs for the U.S. government.',
  DGS3MO: 'Yield on a 3-month U.S. Treasury bill. A common benchmark for short-term cash and money-market rates.',
  DGS6MO: 'Yield on a 6-month U.S. Treasury bill — slightly further out than 3-month but still short-term.',
  DGS1: 'Yield on a 1-year U.S. Treasury security. Bridges short-term money rates and the start of the bond yield curve.',
  DGS2: 'Yield on a 2-year U.S. Treasury note. Very sensitive to Fed policy expectations over the next couple of years.',
  DGS5: 'Yield on a 5-year U.S. Treasury note. Often used to gauge medium-term growth and inflation expectations.',
  DGS10: 'Yield on a 10-year U.S. Treasury note. The world\'s most-watched benchmark — drives mortgage rates and is a key gauge of long-term growth and inflation.',
  DGS30: 'Yield on a 30-year U.S. Treasury bond. The longest standard maturity; reflects very long-term inflation and fiscal expectations.',
};

const CATEGORY_DESCRIPTIONS = {
  Policy: 'Rates the Federal Reserve sets or directly targets to steer the economy.',
  Money: 'Short-term funding rates used by banks, businesses and consumers — strongly influenced by Fed policy.',
  Treasury: 'Yields on U.S. government debt across maturities. Together they form the "yield curve" and price most other interest rates.',
};

const fmtPct = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}%`);
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};

const DeltaCell = ({ latest, previous }) => {
  if (latest === null || latest === undefined || previous === null || previous === undefined) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  const diff = latest - previous;
  const bps = Math.round(diff * 100);
  if (bps === 0) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <RemoveIcon fontSize="small" color="disabled" />
        <Typography variant="body2" color="text.secondary">0 bps</Typography>
      </Stack>
    );
  }
  const up = bps > 0;
  return (
    <Stack direction="row" alignItems="center" spacing={0.25}>
      {up ? (
        <ArrowDropUpIcon fontSize="small" sx={{ color: 'success.main' }} />
      ) : (
        <ArrowDropDownIcon fontSize="small" sx={{ color: 'error.main' }} />
      )}
      <Typography variant="body2" sx={{ color: up ? 'success.main' : 'error.main' }}>
        {up ? '+' : ''}{bps} bps
      </Typography>
    </Stack>
  );
};

const FedRates = () => {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [years, setYears] = useState(2);
  const [selectedSeries, setSelectedSeries] = useState(DEFAULT_SELECTED);
  const [reloadKey, setReloadKey] = useState(0);
  const [glossaryOpen, setGlossaryOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE_URL}/api/fed-rates?years=${years}`)
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body?.error || `HTTP ${r.status}`);
        return body;
      })
      .then((body) => { if (!cancelled) { setData(body); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [years, reloadKey]);

  // Build a merged-by-date dataset for the line chart from currently selected series
  const chartData = useMemo(() => {
    if (!data?.series) return [];
    const byDate = new Map();
    data.series
      .filter((s) => selectedSeries.includes(s.id))
      .forEach((s) => {
        for (const obs of s.observations || []) {
          if (obs.value === null) continue;
          if (!byDate.has(obs.date)) byDate.set(obs.date, { date: obs.date });
          byDate.get(obs.date)[s.id] = obs.value;
        }
      });
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data, selectedSeries]);

  const toggleSeries = (id) => {
    setSelectedSeries((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const groupedSeries = useMemo(() => {
    const groups = { Policy: [], Money: [], Treasury: [] };
    (data?.series || []).forEach((s) => {
      if (!groups[s.category]) groups[s.category] = [];
      groups[s.category].push(s);
    });
    return groups;
  }, [data]);

  const renderSortedTooltip = ({ active, payload, label }) => {
    if (!active || !Array.isArray(payload) || payload.length === 0) return null;

    const sortedPayload = [...payload]
      .filter((item) => item && item.value !== null && item.value !== undefined)
      .sort((a, b) => {
        const aValue = Number(a.value);
        const bValue = Number(b.value);
        const aFinite = Number.isFinite(aValue);
        const bFinite = Number.isFinite(bValue);

        if (!aFinite && !bFinite) return 0;
        if (!aFinite) return 1;
        if (!bFinite) return -1;
        return bValue - aValue;
      });

    if (sortedPayload.length === 0) return null;

    return (
      <Box
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17,24,39,0.96)' : '#fff',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          px: 1.5,
          py: 1,
          minWidth: 220,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {fmtDate(label)}
        </Typography>
        <Stack spacing={0.3}>
          {sortedPayload.map((entry) => (
            <Typography
              key={`${entry.dataKey}-${label}`}
              variant="body2"
              sx={{ color: entry.color || 'text.primary', fontWeight: 500 }}
            >
              {entry.name} : {fmtPct(entry.value)}
            </Typography>
          ))}
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }}
             justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AccountBalanceIcon color="primary" />
            <Typography variant="h5" component="h2">Federal Reserve Interest Rates</Typography>
            <Tooltip title="What do these rates mean?">
              <IconButton size="small" onClick={() => setGlossaryOpen(true)} aria-label="Glossary">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Selected H.15 series sourced from the&nbsp;
            <MuiLink href="https://www.federalreserve.gov/releases/h15/" target="_blank" rel="noopener noreferrer"
                     underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
              Federal Reserve H.15 release <OpenInNewIcon fontSize="inherit" />
            </MuiLink>
            &nbsp;via FRED. Cached for 6 hours.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <ToggleButtonGroup
            size="small" exclusive color="primary" value={years}
            onChange={(_e, v) => { if (v) setYears(v); }} aria-label="Lookback"
          >
            {LOOKBACK_OPTIONS.map((opt) => (
              <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <span>
              <Button size="small" variant="outlined" startIcon={<RefreshIcon />}
                      onClick={() => setReloadKey((k) => k + 1)} disabled={loading}>
                Reload
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 4 }}>
          <CircularProgress size={20} /> <Typography>Loading rates…</Typography>
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
      )}

      {!loading && !error && data && (
        <>
          {/* Latest values table */}
          <Paper variant="outlined" sx={{ mb: 3 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Show</TableCell>
                    <TableCell>Series</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Latest</TableCell>
                    <TableCell>As of</TableCell>
                    <TableCell align="right">1-period change</TableCell>
                    <TableCell>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.series.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox size="small"
                          checked={selectedSeries.includes(s.id)}
                          onChange={() => toggleSeries(s.id)}
                          sx={{ color: SERIES_COLORS[s.id], '&.Mui-checked': { color: SERIES_COLORS[s.id] } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 10, height: 10, bgcolor: SERIES_COLORS[s.id], borderRadius: '2px' }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{s.label}</Typography>
                          <Tooltip
                            arrow
                            placement="right"
                            title={
                              <Box sx={{ maxWidth: 320 }}>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{s.label}</Typography>
                                <Typography variant="body2">{SERIES_DESCRIPTIONS[s.id] || 'No description available.'}</Typography>
                              </Box>
                            }
                          >
                            <InfoOutlinedIcon fontSize="inherit"
                              sx={{ color: 'text.secondary', cursor: 'help', fontSize: '1rem' }} />
                          </Tooltip>
                          <Chip size="small" variant="outlined" label={s.id} sx={{ fontFamily: 'monospace' }} />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={s.category}
                              color={s.category === 'Policy' ? 'primary' : s.category === 'Money' ? 'secondary' : 'default'}
                              variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {fmtPct(s.latest?.value)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {fmtDate(s.latest?.date)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <DeltaCell latest={s.latest?.value} previous={s.previous?.value} />
                      </TableCell>
                      <TableCell>
                        <MuiLink href={`https://fred.stlouisfed.org/series/${s.id}`} target="_blank"
                                 rel="noopener noreferrer" underline="hover"
                                 sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem' }}>
                          FRED <OpenInNewIcon fontSize="inherit" />
                        </MuiLink>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Line chart */}
          <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">Historical rates — last {years}{years >= 30 ? '+' : ''} year{years === 1 ? '' : 's'}</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={() => setSelectedSeries(DEFAULT_SELECTED)}>Reset</Button>
                <Button size="small" onClick={() => setSelectedSeries(data.series.map((s) => s.id))}>Select all</Button>
                <Button size="small" onClick={() => setSelectedSeries([])}>Clear</Button>
              </Stack>
            </Stack>
            {selectedSeries.length === 0 ? (
              <Alert severity="info">Tick one or more series in the table above to plot them.</Alert>
            ) : (
              <Box sx={{ height: { xs: 320, sm: 460 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} minTickGap={32} />
                    <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                           tickFormatter={(v) => `${v}%`} domain={['auto', 'auto']} />
                    <RTooltip
                      content={renderSortedTooltip}
                    />
                    <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
                    {data.series
                      .filter((s) => selectedSeries.includes(s.id))
                      .map((s) => (
                        <Line key={s.id} type="monotone" dataKey={s.id} name={s.label}
                              stroke={SERIES_COLORS[s.id]} dot={false} strokeWidth={1.75}
                              connectNulls isAnimationActive={false} />
                      ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Data: {data.source}. Generated at {fmtDate(data.generatedAt)} ({data.cached ? 'served from cache' : 'fresh'}).
          </Typography>
        </>
      )}

      {/* Glossary dialog */}
      <Dialog open={glossaryOpen} onClose={() => setGlossaryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <InfoOutlinedIcon color="primary" />
            <span>Interest Rates — Plain English</span>
          </Stack>
          <IconButton onClick={() => setGlossaryOpen(false)} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {['Policy', 'Money', 'Treasury'].map((cat) => {
            const rows = (data?.series || []).filter((s) => s.category === cat);
            if (rows.length === 0) return null;
            return (
              <Box key={cat} sx={{ mb: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Chip size="small" label={cat}
                        color={cat === 'Policy' ? 'primary' : cat === 'Money' ? 'secondary' : 'default'} />
                  <Typography variant="body2" color="text.secondary">
                    {CATEGORY_DESCRIPTIONS[cat]}
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 1 }} />
                <Stack spacing={1.25}>
                  {rows.map((s) => (
                    <Box key={s.id}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 10, height: 10, bgcolor: SERIES_COLORS[s.id], borderRadius: '2px' }} />
                        <Typography variant="subtitle2">{s.label}</Typography>
                        <Chip size="small" variant="outlined" label={s.id} sx={{ fontFamily: 'monospace' }} />
                        {s.latest && (
                          <Typography variant="caption" color="text.secondary">
                            latest {fmtPct(s.latest.value)} on {fmtDate(s.latest.date)}
                          </Typography>
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                        {SERIES_DESCRIPTIONS[s.id] || 'No description available.'}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            );
          })}
          <Typography variant="caption" color="text.secondary">
            Source: Federal Reserve H.15 release via FRED. These are general explanations, not investment advice.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGlossaryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FedRates;
